import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRegisterAreaHeader } from '@/shared/navigation';
import { HeuteDateBand } from './HeuteDateBand';

interface Props {
  date: string;
  today: string;
  /** Stunde in der Objektzeit; bestimmt den Gruß am heutigen Tag. */
  hour: number;
  asOf: string | null;
  facts: ReactNode;
  onDateChange: (date: string) => void;
  onRefresh: () => void;
  refreshing: boolean;
}

const fullDate = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

function greeting(hour: number) {
  if (hour < 11) return 'Guten Morgen.';
  if (hour < 18) return 'Guten Tag.';
  return 'Guten Abend.';
}

/**
 * Wald-Kopf von Heute (Board „Heute“): Datum mit Stand, Gruß oder gewählter Tag, Kennzahlen und
 * das Datumsband. Er ist der Bereichskopf der Seite – die Ersatzleiste des Gerüsts entfällt.
 */
export function HeuteHero({
  date,
  today,
  hour,
  asOf,
  facts,
  onDateChange,
  onRefresh,
  refreshing,
}: Props) {
  useRegisterAreaHeader();
  const isToday = date === today;
  const dayLabel = fullDate.format(new Date(`${date}T00:00:00Z`));
  return (
    <section
      aria-labelledby="heute-title"
      className="on-forest bg-forest-deep rounded-hero relative overflow-hidden px-5 pb-[104px] pt-6 text-white md:px-8"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.035)_0_2px,transparent_2px_24px)]"
      />
      <div className="relative flex flex-wrap items-end justify-between gap-6">
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-mint flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em]">
            <span aria-hidden className="bg-mint rounded-pill size-2" />
            {dayLabel} · {isToday ? 'Heute' : date < today ? 'Rückblick' : 'Vorschau'}
            {asOf ? ` · Stand ${asOf}` : ''}
          </p>
          <h1
            id="heute-title"
            className="font-serif text-[40px] font-bold leading-none tracking-[-0.04em] text-white md:text-[52px]"
          >
            {isToday ? greeting(hour) : dayLabel}
          </h1>
          <div className="flex flex-wrap gap-2">{facts}</div>
        </div>
        <HeuteDateBand
          date={date}
          today={today}
          onDateChange={onDateChange}
          extra={
            <button
              type="button"
              aria-label="Aktualisieren"
              onClick={onRefresh}
              disabled={refreshing}
              className="rounded-pill ml-auto flex size-9 flex-none items-center justify-center border border-white/20 text-white disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                aria-hidden
                className={refreshing ? 'motion-safe:animate-spin' : undefined}
              />
            </button>
          }
        />
      </div>
    </section>
  );
}

export function HeuteFact({ value, label }: { value: number; label: string }) {
  return (
    <span className="rounded-pill flex h-[38px] items-center gap-2 border border-white/15 bg-white/[0.08] px-3.5 text-[13px]">
      <b className="font-serif text-lg">{value}</b>
      {label}
    </span>
  );
}
